/**
 * Crawlable "All articles" index for the two article hubs.
 *
 * Sep 2026 (Jason orphan findings): `/blog` rendered only its first 24
 * posts, with the rest behind a JS "Load more", and `/knowledge-hub`
 * linked none of them — so six articles had no hub link at all and four
 * sat below three inbound links. This block lists every published post
 * as a plain anchor, grouped by category, so discovery never depends on
 * a click. Hub pages only — never the footer.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export const AllArticlesIndex = ({
  heading = "All articles",
  blurb = "Every published guide, grouped by topic.",
  testid = "all-articles-index",
}) => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/blog/posts?limit=50`)
      .then((r) => r.json())
      .then((d) => setPosts(Array.isArray(d?.posts) ? d.posts : []))
      .catch(() => setPosts([]));
  }, []);

  if (!posts.length) return null;

  const groups = posts.reduce((acc, p) => {
    const key = p.category || "Guides";
    (acc[key] = acc[key] || []).push(p);
    return acc;
  }, {});

  return (
    <section className="mt-16 border-t border-slate-200 pt-12" data-testid={testid}>
      <h2 className="text-2xl font-display font-bold text-slate-900">{heading}</h2>
      <p className="mt-2 text-sm text-slate-600">
        {blurb} <span className="text-slate-400">({posts.length} articles)</span>
      </p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
        {Object.keys(groups)
          .sort()
          .map((cat) => (
            <div key={cat}>
              <h3 className="text-[11px] uppercase tracking-[0.18em] font-bold text-cyan-700">{cat}</h3>
              <ul className="mt-3 space-y-2">
                {groups[cat].map((p) => (
                  <li key={p.slug}>
                    <Link
                      to={`/blog/${p.slug}`}
                      data-testid={`${testid}-link-${p.slug}`}
                      className="text-sm text-slate-700 hover:text-cyan-700 hover:underline underline-offset-2 leading-snug"
                    >
                      {p.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </div>
    </section>
  );
};

export default AllArticlesIndex;
